import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import cookieBot from "./cookieBot"

import * as Gatsby from "gatsby"

import { TransformLinkUri } from "./link"
import { useSiteUrl } from "../hooks/use-site-url"

const SiteMetadata = ({
  defaultTitle,
  title,
  canonicalLink,
  imageURL,
  language,
  description,
  favicon,
  appleFavicon,
  allLanguage,
  currentURL,
  location,
  mode,
  noAnalytics,
  fallbackLanguagePrefixes = [],
}) => {
  const siteUrl = useSiteUrl() || location.origin || "http://localhost:8000"
  const defaultLang = process.env.GATSBY_DEFAULT_LANG
    ? process.env.GATSBY_DEFAULT_LANG
    : "de"

  let imageMeta = []
  let langArry = []
  let path = ""

  if (allLanguage) {
    langArry = Object.values(allLanguage)
    path =
      language === defaultLang ? location.pathname : location.pathname.substr(3)
  }

  if (imageURL) {
    imageMeta.push(<meta key="1" name="image" content={imageURL} />)
    imageMeta.push(<meta key="2" property="og:image" content={imageURL} />)
    imageMeta.push(<meta key="3" property="og:image:width" content="512" />)
    imageMeta.push(<meta key="4" property="og:image:height" content="512" />)
  }

  let canonicalLinkMeta = []
  if (canonicalLink) {
    // gatsby-node sets fallback canonicals site-relative ("/en/…"); emit them
    // absolute like the hreflang links.
    const canonicalHref = /^\/(?!\/)/.test(canonicalLink)
      ? `${siteUrl}${canonicalLink}`
      : canonicalLink
    canonicalLinkMeta.push(
      <link key="1" rel="canonical" href={canonicalHref} />
    )
    canonicalLinkMeta.push(
      <meta key="2" property="og:url" content={canonicalHref} />
    )
  }

  // Fallback-only languages (createFallbackPages) serve another language's
  // content with rel=canonical pointing there, so they are not advertised as
  // alternates (shared/i18n/fallback-alternates.js). x-default must not land on
  // such a page either — use the first real alternate instead.
  const alternates = langArry.map((prefix, i) => {
    const lang = i === 0 ? defaultLang : prefix
    return {
      lang,
      href: `${siteUrl}${TransformLinkUri(currentURL, "", lang)}`,
      fallback: fallbackLanguagePrefixes.includes(prefix),
    }
  })
  const realAlternates = alternates.filter((a) => !a.fallback)
  let xDefaultHref = siteUrl + TransformLinkUri(currentURL, "", "de")
  if (alternates.some((a) => a.fallback && a.href === xDefaultHref)) {
    xDefaultHref = realAlternates.length ? realAlternates[0].href : xDefaultHref
  }

  let descriptionMeta = []
  if (description) {
    descriptionMeta.push(
      <meta key="1" name="description" content={description} />
    )
    descriptionMeta.push(
      <meta key="2" property="og:description" content={description} />
    )
  }

  let titleMeta = []
  if (title) {
    titleMeta.push(<meta key="1" property="og:site_name" content={title} />)
    titleMeta.push(<title key="2">{title}</title>)
  }

  // Cookiebot loads standalone, gated purely on the per-site CBID env.
  // No fallback: a missing CBID means Cookiebot does not load on this site
  // (rather than silently attributing consent to another site's domain group).
  const cbid = process.env.GATSBY_COOKIEBOT_CBID
  return (
    <Helmet
      defer={false}
      defaultTitle={defaultTitle}
      titleTemplate={
        defaultTitle !== title ? `%s | ${defaultTitle}` : defaultTitle
      }
    >
      <html lang={language} />
      <link rel="icon" href={favicon} />
      <link rel="apple-touch-icon" href={appleFavicon} />
      <meta name="docsearch:version" content="2.0" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover"
      />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={language} />
      <meta property="product:category" content={mode} />

      {realAlternates.map(({ lang, href }) => (
        <link rel="alternate" key={lang} href={href} hrefLang={lang} />
      ))}

      <link rel="alternate" hreflang="x-default" href={xDefaultHref} />
      {/* Cookiebot loader is injected via gatsby-ssr.js (setHeadComponents),
          not here — react-helmet re-executes head scripts on hydration. */}
      {cbid && cookieBot(noAnalytics)}

      {titleMeta}
      {descriptionMeta}
      {canonicalLinkMeta}
      {imageMeta}
      <meta name="twitter:card" content="summary" />
    </Helmet>
  )
}

SiteMetadata.propTypes = {
  defaultTitle: PropTypes.string,
  title: PropTypes.string,
  canonicalLink: PropTypes.string,
  imageURL: PropTypes.string,
  language: PropTypes.string,
  description: PropTypes.string,
  favicon: PropTypes.string,
  appleFavicon: PropTypes.string,
  allLanguage: PropTypes.object,
  fallbackLanguagePrefixes: PropTypes.arrayOf(PropTypes.string),
  currentURL: PropTypes.string.isRequired,
  location: PropTypes.object,
  mode: PropTypes.oneOf([
    "default",
    "group",
    "pharma",
    "food",
    "environment",
    "contact",
    "sustainabilityServices",
  ]).isRequired,
}

export default SiteMetadata
